import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import type React from "react"
import { useMemo, useState } from "react"
import { formatDateOnly } from "../../utils/dates"
import { extraColors } from "../../utils/theme"
import { DrawerChartTitle } from "./precomputedDrawerStyles"
import { NwssSortableTableHeader } from "./NwssSortableTableHeader"
import { nwssScopePrefix, type NwssDrawerSampleScope } from "./nwssDrawerScope"
import { compareNwssCounties, compareNwssSites } from "./useNwssSignalSamples"

export interface NwssEntitySummaryRow {
  key: string
  county?: string | null
  sampleCount: number
  detectionCount: number
  latestSampleDate: string | null
  latestDetectionDate: string | null
  daysSinceLastDetection: number | null
}

type SortKey =
  | "key"
  | "county"
  | "samples"
  | "detections"
  | "latestSampleDate"
  | "latestDetectionDate"
  | "daysSinceLastDetection"

interface EntityBreakdownColumn {
  sortKey: SortKey
  label: string
  align?: "right"
}

export interface NwssEntityBreakdownProps {
  entityLabel: "County" | "Site"
  summaries: NwssEntitySummaryRow[]
  showCountyColumn?: boolean
  sampleScope?: NwssDrawerSampleScope
}

export const NwssEntityBreakdown: React.FC<NwssEntityBreakdownProps> = ({
  entityLabel,
  summaries,
  showCountyColumn = false,
  sampleScope = "historic",
}) => {
  const [orderBy, setOrderBy] = useState<SortKey>("key")
  const [order, setOrder] = useState<"asc" | "desc">("asc")

  const sortedSummaries = useMemo(
    () => sortEntitySummaries(summaries, entityLabel, orderBy, order),
    [summaries, orderBy, order, entityLabel]
  )

  const handleSort = (key: SortKey) => {
    if (orderBy === key) {
      setOrder(prev => (prev === "asc" ? "desc" : "asc"))
      return
    }
    setOrderBy(key)
    setOrder(defaultOrderForSortKey(key))
  }

  if (summaries.length === 0) return null

  return (
    <Box>
      <EntityBreakdownHeader
        entityLabel={entityLabel}
        count={summaries.length}
        sampleScope={sampleScope}
      />
      <Box sx={{ maxHeight: 320, overflow: "auto" }}>
        <EntityBreakdownTable
          entityLabel={entityLabel}
          summaries={sortedSummaries}
          showCountyColumn={showCountyColumn}
          orderBy={orderBy}
          order={order}
          onSort={handleSort}
        />
      </Box>
    </Box>
  )
}

interface EntityBreakdownHeaderProps {
  entityLabel: string
  count: number
  sampleScope: NwssDrawerSampleScope
}

const EntityBreakdownHeader: React.FC<EntityBreakdownHeaderProps> = ({
  entityLabel,
  count,
  sampleScope,
}) => (
  <>
    <DrawerChartTitle>
      {`${nwssScopePrefix(sampleScope)} ${entityLabel.toLowerCase()} summary`} (
      {count})
    </DrawerChartTitle>
    <Typography
      variant="caption"
      sx={{ color: extraColors.translucent50, display: "block", mb: 1 }}
    >
      {entitySummaryCaption(sampleScope)}
    </Typography>
  </>
)

interface EntityBreakdownTableProps {
  entityLabel: "County" | "Site"
  summaries: NwssEntitySummaryRow[]
  showCountyColumn: boolean
  orderBy: SortKey
  order: "asc" | "desc"
  onSort: (key: SortKey) => void
}

const EntityBreakdownTable: React.FC<EntityBreakdownTableProps> = ({
  entityLabel,
  summaries,
  showCountyColumn,
  orderBy,
  order,
  onSort,
}) => (
  <Table size="small" stickyHeader>
    <EntityBreakdownTableHead
      entityLabel={entityLabel}
      showCountyColumn={showCountyColumn}
      orderBy={orderBy}
      order={order}
      onSort={onSort}
    />
    <TableBody>
      {summaries.map(summary => (
        <EntityBreakdownRow
          key={summary.key}
          summary={summary}
          showCountyColumn={showCountyColumn}
        />
      ))}
    </TableBody>
  </Table>
)

interface EntityBreakdownTableHeadProps {
  entityLabel: "County" | "Site"
  showCountyColumn: boolean
  orderBy: SortKey
  order: "asc" | "desc"
  onSort: (key: SortKey) => void
}

const EntityBreakdownTableHead: React.FC<EntityBreakdownTableHeadProps> = ({
  entityLabel,
  showCountyColumn,
  orderBy,
  order,
  onSort,
}) => (
  <TableHead>
    <TableRow>
      {entityBreakdownColumns(entityLabel, showCountyColumn).map(column => (
        <NwssSortableTableHeader
          key={column.sortKey}
          label={column.label}
          align={column.align}
          active={orderBy === column.sortKey}
          order={order}
          onClick={() => onSort(column.sortKey)}
        />
      ))}
    </TableRow>
  </TableHead>
)

interface EntityBreakdownRowProps {
  summary: NwssEntitySummaryRow
  showCountyColumn: boolean
}

const EntityBreakdownRow: React.FC<EntityBreakdownRowProps> = ({
  summary,
  showCountyColumn,
}) => (
  <TableRow>
    <TableCell sx={{ color: extraColors.white, fontWeight: 600 }}>
      {summary.key}
    </TableCell>
    {showCountyColumn && (
      <TableCell sx={{ color: extraColors.translucent50 }}>
        {summary.county ?? "—"}
      </TableCell>
    )}
    <TableCell align="right" sx={{ color: extraColors.white }}>
      {summary.sampleCount.toLocaleString()}
    </TableCell>
    <TableCell align="right" sx={detectionCountCellSx(summary.detectionCount)}>
      {summary.detectionCount.toLocaleString()}
    </TableCell>
    <TableCell sx={{ color: extraColors.translucent50, whiteSpace: "nowrap" }}>
      {formatEntityDate(summary.latestSampleDate)}
    </TableCell>
    <TableCell sx={{ color: extraColors.translucent50, whiteSpace: "nowrap" }}>
      {formatEntityDate(summary.latestDetectionDate)}
    </TableCell>
    <TableCell align="right" sx={{ color: extraColors.translucent50 }}>
      {formatDaysSinceLastDetection(summary.daysSinceLastDetection)}
    </TableCell>
  </TableRow>
)

const compareEntityKeys = (
  entityLabel: "County" | "Site",
  a: NwssEntitySummaryRow,
  b: NwssEntitySummaryRow
): number =>
  entityLabel === "Site"
    ? compareNwssSites(a.key, b.key)
    : compareNwssCounties(a.key, b.key)

const sortValueForKey = (
  row: NwssEntitySummaryRow,
  orderBy: SortKey
): number | string => {
  switch (orderBy) {
    case "key":
      return row.key
    case "county":
      return row.county ?? ""
    case "samples":
      return row.sampleCount
    case "detections":
      return row.detectionCount
    case "latestSampleDate":
      return row.latestSampleDate ?? ""
    case "latestDetectionDate":
      return row.latestDetectionDate ?? ""
    case "daysSinceLastDetection":
      return row.daysSinceLastDetection ?? -1
  }
}

const sortEntitySummaries = (
  summaries: NwssEntitySummaryRow[],
  entityLabel: "County" | "Site",
  orderBy: SortKey,
  order: "asc" | "desc"
): NwssEntitySummaryRow[] =>
  [...summaries].sort((a, b) => {
    if (orderBy === "key") {
      const cmp = compareEntityKeys(entityLabel, a, b)
      return order === "asc" ? cmp : -cmp
    }
    const av = sortValueForKey(a, orderBy)
    const bv = sortValueForKey(b, orderBy)
    if (av < bv) return order === "asc" ? -1 : 1
    if (av > bv) return order === "asc" ? 1 : -1
    return compareEntityKeys(entityLabel, a, b)
  })

const defaultOrderForSortKey = (key: SortKey): "asc" | "desc" => {
  if (
    key === "samples" ||
    key === "detections" ||
    key === "daysSinceLastDetection"
  ) {
    return "desc"
  }
  return "asc"
}

const entityBreakdownColumns = (
  entityLabel: "County" | "Site",
  showCountyColumn: boolean
): EntityBreakdownColumn[] => [
  { sortKey: "key", label: entityLabel },
  ...(showCountyColumn
    ? [{ sortKey: "county" as const, label: "County" }]
    : []),
  { sortKey: "samples", label: "Samples", align: "right" },
  { sortKey: "detections", label: "Detections", align: "right" },
  { sortKey: "latestSampleDate", label: "Last sample" },
  { sortKey: "latestDetectionDate", label: "Last detection" },
  {
    sortKey: "daysSinceLastDetection",
    label: "Days since last detection",
    align: "right",
  },
]

const detectionCountCellSx = (detectionCount: number) => ({
  color:
    detectionCount > 0
      ? extraColors.status.redLight
      : extraColors.translucent50,
  fontWeight: detectionCount > 0 ? 700 : 400,
})

const formatEntityDate = (date: string | null): string =>
  date ? formatDateOnly(date) : "—"

const formatDaysSinceLastDetection = (days: number | null): string => {
  if (days == null) return "—"
  if (days === 0) return "0"
  if (days === 1) return "1"
  return String(days)
}

const entitySummaryCaption = (scope: NwssDrawerSampleScope): string => {
  const daysNote =
    " Days since last detection are through the latest sample in this view."
  if (scope === "event") {
    return `${nwssScopePrefix(scope)} per-entity totals for detections that define this signal.${daysNote}`
  }
  return `${nwssScopePrefix(scope)} per-entity totals for the loaded sample history.${daysNote}`
}
